// Configuration - Update these values for your environment
const CONFIG = {
  // API endpoints for SWA managed API
  UPLOAD_DOCUMENT_API_URL: '/api/uploadDocument',
  EXTRACT_PO_API_URL: '/api/extractPO',
  EXTRACT_PO_STATUS_API_URL: '/api/extractPOStatus',
  VALIDATE_SHIPTO_API_URL: '/api/validateShipTo',
  VALIDATE_ITEMS_API_URL: '/api/validateItems',
  SUBMIT_ORDER_API_URL: '/api/submitOrder',
  SEARCH_ITEMS_API_URL: '/api/searchItems',
  VALIDATE_PRICE_API_URL: '/api/validatePrice',
  SEARCH_CUSTOMER_SHIPTO_API_URL: '/api/searchCustomerShipTo',
  LOG_ACTION_API_URL: '/api/logAction',
  ORDER_DEFAULTS_API_URL: '/api/orderDefaults',
  CHECK_PO_NUMBER_API_URL: '/api/checkPONumber',
  RECHECK_INVENTORY_API_URL: '/api/recheckInventory',
  ITEM_UOMS_API_URL: '/api/itemUoms',
  VENDOR_LOOKUP_API_URL: '/api/vendorLookup',
};

// Utility function to convert file to base64
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// API Service
export const apiService = {
  // Upload document to blob storage and get preview URL
  async uploadDocument(base64Content, fileName, mimeType, userId, userEmail, source = 'web') {
    const payload = {
      fileName: fileName,
      content: base64Content,
      mimeType: mimeType || 'application/octet-stream',
      userId: userId,
      userEmail: userEmail,
      // Origin flag ('outlook' vs 'web') — lets the backend tell Outlook-add-in uploads
      // apart from direct site uploads in telemetry. Purely informational for now.
      source: source
    };

    const response = await fetch(CONFIG.UPLOAD_DOCUMENT_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Upload failed: ${response.status} ${response.statusText} Target URL: ${CONFIG.UPLOAD_DOCUMENT_API_URL}`);
    }

    return await response.json();
  },

  // Extract PO data with correlation tracking
  async extractPO(base64Content, fileName, mimeType, correlationId, userId, userEmail) {
    const payload = {
      message: "Extract this file",
      attachments: [
        {
          name: fileName,
          content: base64Content,
          mimeType: mimeType || 'application/pdf'
        }
      ],
      correlationId: correlationId,
      userId: userId,
      userEmail: userEmail,
      action: "extract"
    };

    const response = await fetch(CONFIG.EXTRACT_PO_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Extraction failed: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  },

  async pollExtractPOStatus(correlationId) {
      const response = await fetch(
          `${CONFIG.EXTRACT_PO_STATUS_API_URL}/${correlationId}`,
          { method: 'GET', credentials: 'include' }
      );
      if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `Status check failed: ${response.status}`);
      }
      return await response.json();
  },

  async extractPOWithPolling(base64Content, fileName, mimeType, correlationId, userId, userEmail, onStatusUpdate) {
      // Step 1: Start extraction
      const startResult = await this.extractPO(base64Content, fileName, mimeType, correlationId, userId, userEmail);

      // Fast path: sync provider returned completed immediately
      if (startResult.status === 'completed' || startResult.po_data) {
          return startResult;
      }

      // Step 2: Poll until complete (fallback only — the fast path above covers the common case)
      const maxAttempts = 36; // ~3 minutes; first check after 1s, then 5s intervals
      for (let i = 0; i < maxAttempts; i++) {
          await new Promise(resolve => setTimeout(resolve, i === 0 ? 1000 : 5000));

          if (onStatusUpdate) onStatusUpdate(i + 1);

          const statusResult = await this.pollExtractPOStatus(correlationId);

          if (statusResult.status === 'completed') {
              return statusResult;
          }
          if (statusResult.status === 'failed') {
              throw new Error(statusResult.error || 'Extraction failed');
          }
          // Still processing — continue polling
      }
      throw new Error('Extraction timed out after 3 minutes');
  },

  // Validate ship-to address against master data
  async validateShipTo(poData, correlationId, userId, userEmail) {
    const payload = {
      correlationId: correlationId,
      userId: userId,
      userEmail: userEmail,
      po_data: poData
    };

    const response = await fetch(CONFIG.VALIDATE_SHIPTO_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Ship-to validation failed: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  },

  // Validate items and pricing against master data
  async validateItems(items, customerNumber, shiptoNumber, shiptoConfidence, correlationId, userId, userEmail) {
    const payload = {
      correlationId: correlationId,
      userId: userId,
      userEmail: userEmail,
      items: items,
      customer_number: customerNumber,
      shipto_number: shiptoNumber,
      shipto_confidence: shiptoConfidence
    };

    const response = await fetch(CONFIG.VALIDATE_ITEMS_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Item validation failed: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  },

  // Search for items in A+ item master (manual CSR lookup)
  async searchItems(query, customerNumber, shiptoNumber, correlationId, userId, userEmail, warehouseId, itembalOnly = false) {
    const payload = {
      correlationId: correlationId,
      userId: userId,
      userEmail: userEmail,
      query: query,
      customer_number: customerNumber,
      shipto_number: shiptoNumber,
      warehouse_id: warehouseId || null,
      // Hide items not set up in warehouse_id (backend drops them before its top-10 slice).
      itembal_only: !!itembalOnly
    };

    const response = await fetch(CONFIG.SEARCH_ITEMS_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Item search failed: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  },

  // Resolve or validate the A+ vendor for a drop-ship line item.
  //  - Pass { vendorNumber } to check a CSR-entered vendor exists in VENDR.
  //  - Pass { itemNumber, warehouseId } to resolve the item's default vendor.
  // Returns { exists, vendorNumber, vendorName }. Display/validate only — never
  // sent to A+ on the order.
  async lookupVendor({ itemNumber, vendorNumber, warehouseId, correlationId }) {
    const payload = { correlationId };
    if (vendorNumber) payload.vendorNumber = vendorNumber;
    if (itemNumber) payload.itemNumber = itemNumber;
    if (warehouseId) payload.warehouseId = warehouseId;

    const response = await fetch(CONFIG.VENDOR_LOOKUP_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Vendor lookup failed: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  },

  // Validate price for a single item against customer contract pricing
  async validatePrice(itemNumber, customerNumber, quantity, poUnitPrice, poUnitOfMeasure, stockUnitOfMeasure, correlationId, userId, userEmail) {
    const payload = {
      correlationId: correlationId,
      userId: userId,
      userEmail: userEmail,
      item_number: itemNumber,
      customer_number: customerNumber,
      quantity: quantity,
      po_unit_price: poUnitPrice,
      po_unit_of_measure: poUnitOfMeasure,
      stock_unit_of_measure: stockUnitOfMeasure
    };

    const response = await fetch(CONFIG.VALIDATE_PRICE_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Price validation failed: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  },

  // Check if PO number already exists for a customer
  async checkPONumber(poNumber, customerNumber, correlationId) {
    const response = await fetch(CONFIG.CHECK_PO_NUMBER_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ poNumber, customerNumber, correlationId }),
    });

    if (!response.ok) {
      throw new Error(`PO number check failed: ${response.status}`);
    }

    return await response.json();
  },

  // Search for customers in A+ customer master
  async searchCustomers(query, correlationId, userId, userEmail) {
    const payload = {
      correlationId: correlationId,
      userId: userId,
      userEmail: userEmail,
      mode: 'customers',
      query: query
    };

    const response = await fetch(CONFIG.SEARCH_CUSTOMER_SHIPTO_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Customer search failed: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  },

  // List ship-to locations for a customer
  async searchShipTos(customerNumber, correlationId, userId, userEmail) {
    const payload = {
      correlationId: correlationId,
      userId: userId,
      userEmail: userEmail,
      mode: 'shiptos',
      customer_number: customerNumber
    };

    const response = await fetch(CONFIG.SEARCH_CUSTOMER_SHIPTO_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Ship-to search failed: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  },

  // Fetch FOB/Carrier code lists and customer/ship-to defaults
  async fetchOrderDefaults(customerNumber, shiptoNumber, correlationId) {
    const payload = {
      correlationId: correlationId,
      customer_number: customerNumber,
      shipto_number: shiptoNumber,
    };

    const response = await fetch(CONFIG.ORDER_DEFAULTS_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Order defaults failed: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  },

  // Recheck inventory at a specific warehouse
  async recheckInventory(warehouseId, itemNumbers, correlationId) {
    const payload = {
      correlationId: correlationId,
      warehouse_id: warehouseId,
      item_numbers: itemNumbers,
    };

    const response = await fetch(CONFIG.RECHECK_INVENTORY_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Inventory recheck failed: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  },

  // Submit order to ERP with audit logging
  async submitToERP(orderData, correlationId, userId, userEmail, processingIntelligence) {
    const payload = {
      correlationId: correlationId,
      userId: userId,
      userEmail: userEmail,
      orderData: orderData,
      ...(processingIntelligence && { processingIntelligence }),
    };

    const response = await fetch(CONFIG.SUBMIT_ORDER_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.message || `Submission failed: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  },

  // Query a PI dashboard view with optional filters
  async fetchDashboardView(viewName, filters = {}) {
    const response = await fetch(`/api/dashboard/${viewName}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(filters),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || `Dashboard query failed: ${response.status}`);
    }
    return await response.json();
  },

  // Log CSR action to Processing Intelligence (fire-and-forget, never blocks UI)
  logAction(correlationId, userId, userEmail, action) {
    fetch(CONFIG.LOG_ACTION_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ correlationId, userId, userEmail, action }),
    }).catch(err => console.warn('PI: failed to log action:', err));
  },

  // === UOM Admin API ===

  // Fetch all UOM equivalence groups (admin)
  async getUomGroups() {
    const response = await fetch('/api/manage/uom', {
      method: 'GET',
      credentials: 'include',
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to fetch UOM groups: ${response.status}`);
    }
    return await response.json();
  },

  // Save (upsert) a UOM equivalence group (admin)
  async saveUomGroup(canonical, description, variants) {
    const response = await fetch('/api/manage/uom', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ canonical, description, variants }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to save UOM group: ${response.status}`);
    }
    return await response.json();
  },

  // Delete an entire UOM equivalence group (admin)
  async deleteUomGroup(canonical) {
    const response = await fetch('/api/manage/uom', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ canonical }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to delete UOM group: ${response.status}`);
    }
    return await response.json();
  },

  // Fetch valid A+ UOMs for a specific item (ITMST IMUNM1/2/3 + IMAUM)
  async getItemUoms(itemNumber) {
    const response = await fetch(
      `${CONFIG.ITEM_UOMS_API_URL}?itemNumber=${encodeURIComponent(itemNumber)}`,
      { method: 'GET', credentials: 'include' }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to fetch item UOMs: ${response.status}`);
    }
    return await response.json(); // { item_number, uoms: [{code, description, conversion_factor}] }
  },

  // Fetch the UOM canonical map (for PriceResolutionModal)
  async getUomMap() {
    const response = await fetch('/api/uom-map', {
      method: 'GET',
      credentials: 'include',
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to fetch UOM map: ${response.status}`);
    }
    return await response.json();
  },
};
